export const technicalDocumentationModule = {
  id: "technical-documentation-enterprise",
  title: "Technical Documentation & Knowledge Management",
  description: "Create comprehensive technical documentation systems that support enterprise software development and compliance requirements.",
  content: `
## What You'll Learn
By the end of this module, you will be able to:
- Design documentation architectures that scale with enterprise growth
- Implement documentation-as-code practices for maintainable technical content
- Create API documentation that accelerates developer adoption
- Build knowledge management systems that capture and share institutional knowledge

## Prerequisites
- Software development experience
- Understanding of enterprise software architecture
- Basic knowledge of documentation tools and formats

## Overview
Poor documentation costs enterprises an average of $62M annually in lost productivity and rework. This module teaches systematic approaches to technical documentation that become assets rather than burdens.

---

## Key Concepts

### Documentation Architecture

#### Information Architecture
**User-Centered Design**: Documentation organized by user needs and workflows
**Progressive Disclosure**: Layered information from overview to implementation details
**Cross-References**: Linked content that supports different reading paths
**Search and Discovery**: Findable content through metadata and taxonomy

#### Documentation Types
**API Documentation**: Interface specifications, examples, and integration guides
**Architecture Documentation**: System design decisions and technical context
**Operational Documentation**: Deployment, monitoring, and troubleshooting guides
**Compliance Documentation**: Audit trails, control descriptions, and evidence

### Documentation-as-Code

#### Version Control Integration
**Source Control**: Documentation lives alongside code in Git repositories
**Review Processes**: Documentation changes follow same review as code changes
**Automated Publishing**: CI/CD pipelines generate and deploy documentation
**Change Tracking**: Full history of documentation evolution

#### Collaborative Authoring
**Markdown Standards**: Consistent formatting and structure across teams
**Template Systems**: Standardized document types and sections
**Content Validation**: Automated checks for completeness and accuracy
**Community Contributions**: Enable team members to improve documentation

---

## Practical Examples

### Example 1: API Documentation Strategy
Create comprehensive API documentation that accelerates developer adoption.

**Documentation Components**:
- Getting started guide with authentication setup
- Interactive API explorer with live examples
- SDK and client library documentation
- Error codes and troubleshooting guide
- Rate limiting and usage guidelines

**OpenAPI Specification**:
Use OpenAPI 3.0 to generate consistent, interactive documentation from code annotations.

### Example 2: Architecture Decision Records (ADRs)
Document architectural decisions with context and consequences.

**ADR Template**:
- Title: Short descriptive name for the decision
- Status: Proposed, accepted, deprecated, superseded
- Context: Forces and constraints that led to the decision
- Decision: What was decided and why
- Consequences: Positive and negative outcomes

---

## Assessment

This module establishes documentation practices that support enterprise scalability, compliance, and knowledge sharing.
`,
  duration: 60
};